import { isDebtAccount } from './accountTypes';

/**
 * What the accounts add up to: the assets, the debt, and assets minus debt.
 *
 * Balances come from the Accounts page's per-account stats, where a debt
 * account's balance is already a positive magnitude ("you owe X"), so debt is
 * subtracted rather than added. The magnitude is taken again here so a debt
 * balance that arrives signed cannot turn into an asset. Closed accounts are
 * left out, as the desktop header leaves them out: their balances are often
 * stale.
 *
 * This is not the desktop's "Net worth" figure, which shows the assets total.
 *
 * @param {Array} accounts - accounts with `id`, `account_group`, `account_type`, `is_active`
 * @param {Map} stats - account id → `{ balance }`
 * @returns {{ assets: number, debt: number, net: number }}
 */
export function assetsMinusDebt(accounts, stats) {
  let assets = 0;
  let debt = 0;
  (accounts || []).forEach((a) => {
    if (a.is_active === false) return;
    const balance = stats?.get(a.id)?.balance || 0;
    if (isDebtAccount(a.account_group, a.account_type)) debt += Math.abs(balance);
    else assets += balance;
  });
  return { assets, debt, net: assets - debt };
}

/**
 * The same figures per currency, for a workspace that holds several and shows
 * them unconverted. `perCurrencyStats` maps account id → `[{ currency, balance }]`.
 * Currencies come out in the order they are first met.
 *
 * @returns {Array<{ currency: string, assets: number, debt: number, net: number }>}
 */
export function assetsMinusDebtByCurrency(accounts, perCurrencyStats) {
  const byCurrency = new Map();
  (accounts || []).forEach((a) => {
    if (a.is_active === false) return;
    const isDebt = isDebtAccount(a.account_group, a.account_type);
    (perCurrencyStats?.get(a.id) || []).forEach((line) => {
      const entry = byCurrency.get(line.currency) || { currency: line.currency, assets: 0, debt: 0 };
      if (isDebt) entry.debt += Math.abs(line.balance || 0);
      else entry.assets += line.balance || 0;
      byCurrency.set(line.currency, entry);
    });
  });
  return [...byCurrency.values()].map((e) => ({ ...e, net: e.assets - e.debt }));
}
